import type { FetchZoneListParams, Zone } from 'Services/ControlDataManagement/Features/Zone/types'
import type { ListResponse } from 'Services/ControlDataManagement/types'
import { createBaseApi } from 'Util/ApiClient'
import { RTKCacheTags } from 'Util/constants'

enum TagTypes {
  ZONE = 'Zone'
}

export const zoneApi = createBaseApi({
  reducerPath: 'zoneApi',
  keepUnusedDataFor: 5,
  tagTypes: [TagTypes.ZONE]
}).injectEndpoints({
  endpoints: builder => ({
    // biome-ignore lint/suspicious/noConfusingVoidType: RTK query requires void type for no params
    getZones: builder.query<ListResponse<Zone>, FetchZoneListParams | void>({
      query: params => ({
        url: '/api/v1/zone/',
        params: params ?? undefined
      }),
      providesTags: result =>
        result
          ? [
              ...result.resources.map(
                ({ zone_id }) => ({ type: TagTypes.ZONE, id: zone_id }) as const
              ),
              { type: TagTypes.ZONE, id: RTKCacheTags.LIST }
            ]
          : [{ type: TagTypes.ZONE, id: RTKCacheTags.LIST }]
    }),
    getSingleZone: builder.query<Zone, string>({
      query: zone_id => `/api/v1/zone/${zone_id}/`,
      providesTags: (_result, _error, zone_id) => [{ type: TagTypes.ZONE, id: zone_id }]
    }),
    createSingleZone: builder.mutation<Zone, Partial<Zone>>({
      query: data => ({
        url: `/api/v1/zone/`,
        method: 'POST',
        body: data
      }),
      invalidatesTags: [{ type: TagTypes.ZONE, id: RTKCacheTags.LIST }]
    }),
    updateSingleZone: builder.mutation<Zone, { zone_id: string } & Partial<Zone>>({
      query: ({ zone_id, ...data }) => ({
        url: `/api/v1/zone/${zone_id}/`,
        method: 'PUT',
        body: data
      }),
      invalidatesTags: (_result, _error, { zone_id }) => [{ type: TagTypes.ZONE, id: zone_id }]
    }),
    deleteSingleZone: builder.mutation<Record<string, never>, string>({
      query: id => ({
        url: `/api/v1/zone/${id}/`,
        method: 'DELETE'
      }),
      invalidatesTags: [{ type: TagTypes.ZONE, id: RTKCacheTags.LIST }]
    })
  })
})

export const {
  useGetZonesQuery,
  useGetSingleZoneQuery,
  useCreateSingleZoneMutation,
  useUpdateSingleZoneMutation,
  useDeleteSingleZoneMutation
} = zoneApi
